import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, ChevronRight, Search, Star } from "lucide-react";
import { api } from "../lib/api";
import { BusinessCard } from "../components/BusinessCard";

export default function CityLanding() {
  const { citySlug, categorySlug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.landing(citySlug, categorySlug)
      .then(setData)
      .catch(() => setNotFound(true));
  }, [citySlug, categorySlug]);

  useEffect(() => {
    if (!data) return;
    const place = data.market.label;
    const cat = data.category ? `${data.category.name} en ` : "Negocios latinos en ";
    const title = `${cat}${place} | MiradaLatina`;
    document.title = title;
    const desc = `Encuentra ${data.count} ${data.category ? data.category.name.toLowerCase() : "negocios latinos"} en ${place}. Directorio en español con fichas verificadas, teléfono, dirección y horarios.`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    meta.content = desc;
    return () => { document.title = "MiradaLatina"; };
  }, [data]);

  if (notFound)
    return (
      <div className="flex flex-col items-center py-24 text-center">
        <p className="font-display text-xl font-bold">Página no encontrada</p>
        <Link to="/explorar" className="mt-3 rounded-full bg-brand px-5 py-2 text-sm font-bold text-white">Ir al directorio</Link>
      </div>
    );
  if (!data) return <div className="py-24 text-center text-muted-foreground">Cargando...</div>;

  const place = data.market.label;
  const h1 = data.category ? `${data.category.name} en ${place}` : `Negocios latinos en ${place}`;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {/* Breadcrumbs */}
      <nav className="flex flex-wrap items-center gap-1 text-xs font-medium text-muted-foreground" data-testid="landing-breadcrumbs">
        <Link to="/" className="hover:text-brand">Inicio</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to={`/directorio/${data.market.slug}`} className="hover:text-brand">{place}</Link>
        {data.category && (<><ChevronRight className="h-3 w-3" /><span className="text-brand-ink">{data.category.name}</span></>)}
      </nav>

      {/* Hero */}
      <section className="mt-3 rounded-3xl bg-gradient-to-br from-brand/10 to-brand-gold/5 p-6 sm:p-9 ml-grain">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-wider text-brand">
          <MapPin className="h-3.5 w-3.5" /> {place}
        </span>
        <h1 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-ink sm:text-4xl lg:text-5xl" data-testid="landing-h1">
          {h1}
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Descubre <strong>{data.count}</strong> {data.category ? data.category.name.toLowerCase() : "negocios latinos"} de confianza en {place}.
          Fichas con teléfono, dirección, horario y estado de verificación — todo en español.
        </p>
        <button
          data-testid="landing-search-btn"
          onClick={() => nav(`/explorar?city=${encodeURIComponent(data.market.city)}${data.category ? `&category=${data.category.slug}` : ""}`)}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-hover"
        >
          <Search className="h-4 w-4" /> Buscar y filtrar
        </button>
      </section>

      {/* Category chips within the city */}
      {data.categories_breakdown.length > 0 && (
        <section className="mt-6">
          <h2 className="font-display text-xl font-bold tracking-tight">Categorías en {data.market.city}</h2>
          <div className="ml-scroll mt-3 flex flex-wrap gap-2">
            <Link
              to={`/directorio/${data.market.slug}`}
              data-testid="landing-cat-all"
              className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${!data.category ? "border-brand bg-brand text-white" : "border-border bg-white hover:border-brand/40"}`}
            >
              Todas
            </Link>
            {data.categories_breakdown.map((c) => (
              <Link
                key={c.slug}
                to={`/directorio/${data.market.slug}/${c.slug}`}
                data-testid={`landing-cat-${c.slug}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${data.category?.slug === c.slug ? "border-brand bg-brand text-white" : "border-border bg-white hover:border-brand/40"}`}
              >
                {c.name}
                <span className={`rounded-full px-1.5 text-xs ${data.category?.slug === c.slug ? "bg-white/25" : "bg-secondary"}`}>{c.count}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Business list */}
      <section className="mt-6">
        {data.businesses.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
            Aún no tenemos {data.category ? data.category.name.toLowerCase() : "negocios"} en {place}. ¡Pronto agregaremos más!
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {data.businesses.map((b) => (
              <BusinessCard key={b.id} b={b} onContact={(id, type) => api.track({ business_id: id, type })} />
            ))}
          </div>
        )}
      </section>

      {/* SEO body copy */}
      <section className="mt-10 rounded-3xl bg-card p-6 sm:p-8">
        <h2 className="font-display text-xl font-bold tracking-tight">
          {data.category ? `Sobre ${data.category.name.toLowerCase()} en ${data.market.city}` : `El directorio latino de ${data.market.city}`}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          MiradaLatina conecta a la comunidad latina de {place} con {data.category ? data.category.name.toLowerCase() : "los mejores negocios y servicios"} de confianza.
          Cada ficha incluye datos verificados por nuestros agentes y por los propios dueños, para que encuentres rápido lo que buscas: teléfono, WhatsApp, dirección y horarios.
          {data.category ? "" : " Explora restaurantes, panaderías, barberías, mecánicos, tiendas, servicios legales y mucho más."}
        </p>
      </section>

      {/* Other cities internal links */}
      <section className="mt-8">
        <h2 className="font-display text-xl font-bold tracking-tight">Explora otras ciudades</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {data.other_cities.map((m) => (
            <Link
              key={m.slug}
              to={`/directorio/${m.slug}${data.category ? `/${data.category.slug}` : ""}`}
              data-testid={`landing-city-${m.slug}`}
              className="group flex items-center justify-between rounded-2xl border border-border bg-card px-4 py-3 transition-all hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-md"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-brand-ink">
                <MapPin className="h-4 w-4 text-brand" /> {m.label}
              </span>
              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-bold text-muted-foreground">{m.count}</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
